import React from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Button, Cell, Row, SectionHeader, StatusPayment, Text, TooltipIcon, TooltipWrapper, User } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { IPayout } from 'types';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';
import { formatDateAndTime } from 'utils/date';
import { payoutStatusAlertMessage } from 'utils/payout';

// Payments Tab
export const ChartSectionHeader = styled(SectionHeader)`
  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: ${toRem(theme.space['3xl'])};
  }
`;

export const BusinessDayLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: auto;

  > * {
    &:first-child {
      margin-bottom: ${toRem(theme.space.sm)};
    }
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-flow: row nowrap;
    align-items: center;

    > * {
      &:first-child {
        margin-right: ${toRem(theme.space.md)};
        margin-bottom: 0;
      }
    }
  }
`;

export const BusinessDayLabel = styled(Text).attrs({
  as: 'label',
})`
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  font-weight: ${theme.font.weight.medium};
  display: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    font-size: ${toRem(theme.font.size.sm)};
    line-height: ${lineHeight(theme.font.size.sm)};
    display: block;
  }
`;

export const IconContainer = styled.div`
  position: relative;
  justify-content: center;
  align-items: center;
  width: ${toRem(60)};
  height: ${toRem(60)};
  background-color: ${theme.color.secondary[400]};
  box-shadow: 0 0 0 ${toRem(15)} ${theme.color.secondary[25]};
  border-radius: 50%;
  margin-bottom: ${toRem(theme.space['2xl'])};
`;

// Details/Transactions Tab
export const DetailsContentHeader = styled(SectionHeader).attrs({
  afterHeaderCardRow: true,
})`
  padding: 0 ${toRem(theme.space.xl)};

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 ${toRem(theme.space['3xl'])};
  }
`;

interface ITransactionRowProps {
  payout?: IPayout;
  isLoading?: boolean;
  onClick?: () => void;
}

export const TransactionRow = ({ payout, isLoading, onClick, ...rest }: ITransactionRowProps) => (
  <Row clickable onClick={onClick} {...rest} data-value={payout?.id}>
    <Cell data-cy="payee-cell">
      <User id={payout?.id || ''} username={payout?.employee.name || ''} userId={payout?.userId} details={payout?.id} isLoading={isLoading} isDismissed={!!payout?.employee.deletedAt} />
    </Cell>

    <Cell data-cy="status-cell">
      <StatusPayment
        status={payout?.status}
        isLoading={isLoading}
        paymentId={payout?.id}
        {...(payout?.alert && {
          subStatusLabel: payout.alert.label,
          subStatusTooltip: payoutStatusAlertMessage(payout),
        })}
      />
    </Cell>

    <Cell data-cy="business-day-cell">
      {isLoading ? (
        <Skeleton width={100} />
      ) : (
        <TooltipWrapper>
          {payout?.paymentDate}
          <TooltipIcon iconMarginLeft={theme.space.sm} id={payout?.id}>
            <p>
              <strong>Date Submitted</strong>
              <br />
              {dayjs(payout?.createdAt).format('YYYY-MM-DD')}
              <br />
              {dayjs(payout?.createdAt).format('hh:mm:ss A z')}
            </p>
          </TooltipIcon>
        </TooltipWrapper>
      )}
    </Cell>

    <Cell align="left" data-cy="tip-amount-cell">
      {isLoading ? <Skeleton width={100} /> : <strong>{centsToDollarsDisplay((payout?.gross || 0) - (payout?.autoGratuity || 0))}</strong>}
    </Cell>

    <Cell align="left" data-cy="autograts-cell">
      {isLoading ? <Skeleton width={100} /> : <strong>{centsToDollarsDisplay(payout?.autoGratuity)}</strong>}
    </Cell>

    <Cell align="left" data-cy="gross-cell">
      {isLoading ? <Skeleton width={100} /> : <strong>{centsToDollarsDisplay(payout?.gross)}</strong>}
    </Cell>

    <Cell align="left" data-cy="holdback-cell">
      {isLoading ? <Skeleton width={100} /> : <strong>{centsToNegativeDollarsDisplay(payout?.holdback)}</strong>}
    </Cell>

    <Cell align="left" data-cy="paid-cell">
      {isLoading ? <Skeleton width={100} /> : <strong>{centsToDollarsDisplay(payout?.net)}</strong>}
    </Cell>
  </Row>
);

interface IReviewRowProps {
  payout?: IPayout;
  isLoading?: boolean;
  currentEmployeeId?: string;
  onClick?: () => void;
}

export const ReviewRow = ({ payout, isLoading, currentEmployeeId, onClick }: IReviewRowProps) => {
  const sentBy = payout?.statusHistory?.find((status) => status.action === 'create')?.changedBy;
  const isSender = sentBy?.id === currentEmployeeId;
  const isReceiver = payout?.employee.id === currentEmployeeId;

  return (
    <Row data-value={payout?.id} data-cy="payment-review-row">
      <Cell data-cy="payout-id-cell">{isLoading ? <Skeleton width={100} /> : <strong>{payout?.id}</strong>}</Cell>

      <Cell data-cy="payee-cell">
        {isLoading ? (
          <Skeleton width={100} />
        ) : (
          <TooltipWrapper>
            {payout?.employee.name || '--'}
            {isReceiver && (
              <TooltipIcon place="top" iconMarginLeft={theme.space.xs} id={`receiver_disabled_${payout?.id}`} dataCy="receiver-disabled-tooltip">
                You can't review payments you were sent.
              </TooltipIcon>
            )}
          </TooltipWrapper>
        )}
      </Cell>

      <Cell data-cy="sent-by-cell">
        {isLoading ? (
          <Skeleton width={100} />
        ) : (
          <TooltipWrapper>
            {sentBy?.name || '--'}
            {isSender && (
              <TooltipIcon place="top" iconMarginLeft={theme.space.xs} id={`sender_disabled_${payout?.id}`} dataCy="sender-disabled-tooltip">
                You can't review payments you sent.
              </TooltipIcon>
            )}
          </TooltipWrapper>
        )}
      </Cell>

      <Cell data-cy="date-cell">{isLoading ? <Skeleton width={100} /> : payout?.createdAt ? formatDateAndTime(payout?.createdAt) : '--'}</Cell>

      <Cell data-cy="reason-cell">{isLoading ? <Skeleton width={100} /> : payoutStatusAlertMessage(payout) || '--'}</Cell>

      <Cell data-cy="amount-cell">{isLoading ? <Skeleton width={100} /> : payout?.net ? centsToDollarsDisplay(payout?.net) : '--'}</Cell>

      <Cell data-cy="review-cell">{isLoading ? <Skeleton width={100} /> : <Button data-cy="review-button" label="Review" type="button" appearance="primary" disabled={isSender || isReceiver} size="small" onClick={onClick} />}</Cell>
    </Row>
  );
};
